import type { RankingInfo } from '@tanstack/match-sorter-utils'
import { rankings, rankItem } from '@tanstack/match-sorter-utils'
import type { CellContext, ColumnDef, Row, RowData } from '@tanstack/react-table'
import {
  columnFilteringFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  createColumnHelper,
  createFilteredRowModel,
  createSortedRowModel,
  globalFilteringFeature,
  rowPaginationFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_basic,
  sortFn_datetime,
  sortFn_text,
  tableFeatures,
} from '@tanstack/react-table'
import type { ReactNode } from 'react'

export interface TableColumnMeta {
  /** Columns grow into spare width in proportion to their size unless this is `false`. */
  grow?: boolean
  /** Names the column in sort labels when `header` renders markup rather than text. */
  label?: string
  align?: 'left' | 'center' | 'right'
  /** Renders the column's box in the filter row. Its value is in `columnFilters` under the column id. */
  filter?: (props: { value: unknown; onChange: (value: unknown) => void }) => ReactNode
}

// Matches loosely, and records how well each column matched so results can be ranked.
const fuzzy = (row: Row<any, any>, columnId: string, filterValue: unknown, addMeta?: (meta: RankingInfo) => void) => {
  const itemRank = rankItem(row.getValue(columnId), String(filterValue), { threshold: rankings.MATCHES })
  addMeta?.(itemRank)
  return itemRank.passed
}
fuzzy.autoRemove = (value: unknown) => !value

export const appTableFeatures = tableFeatures({
  columnFilteringFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  globalFilteringFeature,
  rowPaginationFeature,
  rowSortingFeature,
  filteredRowModel: createFilteredRowModel(),
  sortedRowModel: createSortedRowModel(),
  filterFns: { fuzzy },
  // The names the `'auto'` sort resolves to.
  sortFns: { alphanumeric: sortFn_alphanumeric, basic: sortFn_basic, datetime: sortFn_datetime, text: sortFn_text },
  columnMeta: {} as TableColumnMeta,
  filterMeta: {} as RankingInfo,
})

export type AppTableFeatures = typeof appTableFeatures
export type TableColumnDef<TData extends RowData> = ColumnDef<AppTableFeatures, TData, any>
export type TableRow<TData extends RowData> = Row<AppTableFeatures, TData>
export type TableCellContext<TData extends RowData, TValue = unknown> = CellContext<AppTableFeatures, TData, TValue>

export const createTableColumnHelper = <TData extends RowData>() => createColumnHelper<AppTableFeatures, TData>()

const bestRank = <TData extends RowData>(row: TableRow<TData>) =>
  Math.max(...Object.values(row.columnFiltersMeta).map(meta => (meta as RankingInfo).rank))

/** Orders search results by their best-matching column, for while no sort is applied. */
export const rankByGlobalMatch = <TData extends RowData>(rows: TableRow<TData>[]) =>
  [...rows].sort((a, b) => bestRank(b) - bestRank(a))
